import { StudentInkCluster, strokeKey } from "./studentInkClusters";

/** Per-canvas record of the handwriting, across turns. */
interface ChangeState {
  /** label → stroke set, as of the last board the model was actually shown. */
  baseline: Map<string, string>;
  /** This turn's board. Promoted to `baseline` only once the model sees it. */
  pending: Map<string, string> | null;
  /** False until a first board has been shown; before that nothing is "new". */
  seeded: boolean;
  /** Last diff, including labels that vanished and so cannot be badged. */
  lastChanged: Set<string>;
}

const states = new WeakMap<object, ChangeState>();

function stateFor(owner: object): ChangeState {
  let state = states.get(owner);
  if (!state) {
    state = {
      baseline: new Map(),
      pending: null,
      seeded: false,
      lastChanged: new Set(),
    };
    states.set(owner, state);
  }
  return state;
}

/**
 * Labels whose stroke set differs from the board the model last saw — grown,
 * shrunk, or new. Shrinking counts: erasing is how a student corrects.
 *
 * Call ONCE per turn (see `AgentHelpers.getStudentInkClusters`) and
 * `commitInkChanges` once the board has actually reached the model.
 */
export function changedInkClusters(
  owner: object,
  clusters: StudentInkCluster[],
): Set<string> {
  const state = stateFor(owner);
  const current = new Map(clusters.map((c) => [c.label, strokeKey(c)]));
  state.pending = current;
  if (!state.seeded) {
    state.lastChanged = new Set();
    return state.lastChanged;
  }

  const changed = new Set<string>();
  for (const [label, key] of current) {
    if (state.baseline.get(label) !== key) changed.add(label);
  }
  // Erasing a whole line removes its label for good, so it is only ever visible
  // from the baseline side. It cannot be badged or cropped, but it is a change.
  for (const label of state.baseline.keys()) {
    if (!current.has(label)) changed.add(label);
  }
  state.lastChanged = changed;
  return changed;
}

/**
 * Promote this turn's board to the baseline. Called only after the screenshot
 * is built, so writing the model never saw is still reported next turn.
 */
export function commitInkChanges(owner: object): void {
  const state = states.get(owner);
  if (!state?.pending) return;
  state.baseline = state.pending;
  state.pending = null;
  state.seeded = true;
}

/**
 * The last diff, including labels that vanished. Lets a caller tell "nothing
 * changed" from "something changed that I cannot point at".
 */
export function lastInkChanges(owner: object): Set<string> {
  return states.get(owner)?.lastChanged ?? new Set();
}

/** Forget what `owner`'s board looked like (test seam). */
export function resetInkChanges(owner: object): void {
  states.delete(owner);
}
